import { Deck, DeckCard } from "@apiserver-rest/ponder-data-client-base";
import { DeckGroup } from "./deck-group";
import { DeckGroupColumn } from "./deck-group-column";

export abstract class DeckGroups {
    public groups: DeckGroup[] = [];
    public columns: DeckGroupColumn[] = []

    public constructor(protected deck: Deck, columnCount: number) {
        this.addDefaultGroups();
        this.addSpecializedGroups();
        this.setColumnCount(columnCount);
    }

    public addCards(cards: DeckCard[]): void {
        if (!cards) {
            return;
        }

        cards.forEach(c => this.addCard(c));
        this.repopulateColumns();
    }
    
    public addCard(card: DeckCard): DeckGroup | undefined {
        var group = this.getGroup(card);

        if (group) {
            group.addCard(card);
            this.repopulateColumns();
        }

        return group;
    }

    public getGroup(card: DeckCard): DeckGroup | undefined {
        for (let i = 0; i < this.groups.length; ++i) {
            let group = this.groups[i];

            if (group.isValid(this.deck, card)) {
                return group;
            }
        }
        
        return undefined;
    }

    public removeCard(card: DeckCard): DeckGroup | undefined {
        for (let i = 0; i < this.groups.length; ++i) {
            if (this.groups[i].removeCard(card)) {
                return this.groups[i];
            }
        }

        return undefined;
    }

    public setColumnCount(columnCount: number): void {
        if (columnCount == this.columns.length) {
            return;
        }

        this.columns = new Array<DeckGroupColumn>(columnCount);

        this.repopulateColumns();
    }

    /**
     * An attempt at "reactive" columns.
     * 
     * Adds groups to available columns and tries to disperse them evenly.
     * It does not break groups apart however. 
     */
    public repopulateColumns(): void {
        if (!this.columns) {
            return;
        }

        this.rebuildColumns();

        const groupCounts = this.getGroupCounts();
        const cardsPerColumn = groupCounts.cardCount / this.columns.length;
        
        var currentColumn = 0;
        var cardsInCurrentColumn = 0;
        var groupsAdded = 0;

        this.groups.forEach(g => {
            if (!g.cards.length) {
                return;
            }

            groupsAdded++;

            if ((currentColumn < (this.columns.length - 1)) &&                                                  // If there are unused columns
                ((cardsInCurrentColumn > cardsPerColumn) || (groupsAdded == groupCounts.groupCount))) {         // If the current column is full OR we are coming up to the last group
                currentColumn++;
                cardsInCurrentColumn = 0;
            }

            this.columns[currentColumn].groups.push(g);
            cardsInCurrentColumn += g.cards.length;
        });
    }

    private rebuildColumns(): void {
        for (let i = 0; i < this.columns.length; ++i) { 
            this.columns[i] = new DeckGroupColumn(i); 
        }
    }
    
    public getGroupCounts(): { cardCount: number, groupCount: number } {
        var cardCount = 0;
        var groupCount = 0;

        this.groups.forEach(g => {
            if (g.cards.length > 0) {
                cardCount += g.cards.length;
                groupCount++;
            }
        })

        return { cardCount, groupCount };
    }

    // -------------------------------------------------------------------------------------
    // Abstract Methods
    // -------------------------------------------------------------------------------------

    protected abstract addDefaultGroups(): void;
    protected abstract addSpecializedGroups(): void;
}